/**
 * Single source of truth for resolving the Supabase access token before an
 * authenticated API call. Used by both the JSON client (apiClient) and the SSE
 * client (sseClient) so the refresh-retry below is applied consistently.
 */

import { supabase } from "@/integrations/supabase/client";

/**
 * Error message thrown when no usable session can be resolved.
 *
 * Kept as the literal "Not authenticated" string on purpose — the Sentry /
 * PostHog classifiers in `src/lib/sentry.ts` (`isExpectedUserError`,
 * `classifyError`) match on it to bucket the error as `unauthenticated`.
 */
export const NOT_AUTHENTICATED_MESSAGE = "Not authenticated";

/**
 * Resolve the current Supabase access token for an authenticated request.
 *
 * Why the explicit refresh fallback: on iOS Safari a frozen/backgrounded tab
 * can leave supabase-js's first `getSession()` returning a null session even
 * though the user is online and their refresh token is still valid — the
 * access token expired while the page was suspended and the auto-refresh that
 * `getSession()` kicks off races/fails on resume. Observed in production as a
 * cluster of `client_error` / `unauthenticated` events, all on Mobile Safari /
 * iOS with `online = true`. A single explicit `refreshSession()` recovers that
 * case. If the refresh token is genuinely dead, `refreshSession()` also yields
 * no token and we throw — the caller surfaces the not-signed-in toast.
 */
export async function getAccessToken(): Promise<string> {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  if (session?.access_token) return session.access_token;

  const {
    data: { session: refreshed },
  } = await supabase.auth.refreshSession();
  if (refreshed?.access_token) return refreshed.access_token;

  throw new Error(NOT_AUTHENTICATED_MESSAGE);
}
